/**
 * The pandit drawer's Availability tab (docs/05-admin/07 §3.2 item 4), read-only.
 *
 * **Built from the spec, not a design** (owner ruling 2026-10-26). The question it answers is "why
 * isn't this pandit getting offers?", without phoning them, so it shows what the matcher reads:
 * a pause in force, the weekly hours the slot generator expands, the exceptions inside the slot
 * horizon, the preference bounds, and how many requests missed them in the last 30 days. All of
 * it from `GET /v1/admin/pandits/{id}/availability`.
 *
 * There is nothing to press. Availability is the pandit's own, edited in the app; the one ops
 * control is the manual slot on the dispatch surface (doc 08 §3.4), and the tab says so.
 */
import { DataTable } from '../components/data';
import { KV } from '../components/entities';
import { EmptyState, ErrorState, LoadingState } from '../components/feedback';
import { Section } from '../components/kit';
import { StatusChip } from '../components/primitives';
import { errorMessage } from '../lib/api/client';
import { usePanditAvailability } from '../lib/api/hooks/pandits';

/** `day_of_week` is Python's `date.weekday()`: 0 is Monday. */
export const WEEKDAYS = ['Monday', 'Tuesday', 'Wednesday', 'Thursday', 'Friday', 'Saturday', 'Sunday'];

/** "06:00:00" → "06:00"; a missing bound is the whole day. */
export function hhmm(value: string | null | undefined): string {
  return value ? value.slice(0, 5) : '';
}

export function exceptionLabel(kind: string, start: string | null | undefined): string {
  if (kind === 'add') return 'Extra hours';
  return start ? 'Hours off' : 'Day off';
}

const yesNo = (value: boolean) => (value ? 'Yes' : 'No');

export function PanditAvailability({ panditId }: { panditId: number }) {
  const view = usePanditAvailability(panditId);

  if (view.isPending) return <LoadingState message="Reading their availability…" />;
  if (view.isError) return <ErrorState title="Could not read their availability" cause={errorMessage(view.error)} />;

  const a = view.data;
  const pref = a.preference ?? null;
  return (
    <>
      <Section title="Can requests reach them?">
        <div style={{ display: 'flex', gap: 'var(--sp-sm)', flexWrap: 'wrap', alignItems: 'center' }}>
          {a.paused_until ? (
            <StatusChip tone="warn" label={`Paused until ${a.paused_until} — no offers reach them`} />
          ) : (
            <StatusChip tone="success" label="Accepting requests" />
          )}
          <StatusChip
            tone={a.missed_offers_count_30d > 0 ? 'warn' : 'neutral'}
            label={`${a.missed_offers_count_30d} requests could not reach them in the last 30 days`}
          />
        </div>
      </Section>

      <Section title="Preference" note="Their own settings, as the matcher reads them">
        {pref === null ? (
          <EmptyState inline icon="calendar-clock" title="No preference saved" body="The matcher applies its defaults until they save one in the app." />
        ) : (
          <KV
            rows={[
              ['Most bookings in a day', String(pref.max_bookings_per_day)],
              ['Notice needed', `${pref.min_advance_notice_hours} hours`],
              ['Books ahead up to', `${pref.max_advance_days} days`],
              ['Takes instant bookings', yesNo(pref.accepts_instant_book)],
              ['Declines outside their hours', yesNo(pref.auto_decline_outside_windows)],
            ]}
          />
        )}
      </Section>

      <Section title="Weekly hours" note="What the slot generator opens" pad={false}>
        {a.rules.length === 0 ? (
          <EmptyState inline icon="clock" title="No weekly hours" body="No slots open for them, so instant booking cannot reach them." />
        ) : (
          <DataTable
            density="compact"
            columns={[
              { key: 'day', label: 'Day' },
              { key: 'hours', label: 'Hours', mono: true },
              { key: 'state', label: 'State' },
            ]}
            rows={a.rules.map((r) => ({
              id: r.id,
              day: WEEKDAYS[r.day_of_week] ?? `Day ${r.day_of_week}`,
              hours: `${hhmm(r.start_time)}–${hhmm(r.end_time)}`,
              state: r.is_active ? 'Active' : 'Off',
            }))}
          />
        )}
      </Section>

      <Section title="Exceptions" note={`Today and the next ${a.horizon_days} days`} pad={false}>
        {a.exceptions.length === 0 ? (
          <EmptyState inline icon="calendar-check" title="No exceptions" body="Their weekly hours stand as they are." />
        ) : (
          <DataTable
            density="compact"
            columns={[
              { key: 'on', label: 'Date', mono: true },
              { key: 'what', label: 'Change' },
              { key: 'hours', label: 'Hours', mono: true },
              { key: 'reason', label: 'Reason' },
            ]}
            rows={a.exceptions.map((e) => ({
              id: e.id,
              on: e.exception_on,
              what: exceptionLabel(e.kind, e.start_time),
              hours: e.start_time ? `${hhmm(e.start_time)}–${hhmm(e.end_time)}` : 'All day',
              reason: e.reason ?? '—',
            }))}
          />
        )}
      </Section>

      <p style={{ margin: 0, fontSize: 'var(--fs-admin-cell)', color: 'var(--c-ink2)' }}>
        Availability is theirs to change, in the app. To open a start their hours do not cover, add a
        manual slot from the dispatch board.
      </p>
    </>
  );
}
